import React, { useState, useRef, useEffect } from 'react';
import {
    AppDivider,
    AppOptionsButton,
    AppOptionButton,
    AppOptionIconContainer,
    OpenAppOptionsButtonSize,
} from 'components/atoms/CommonAppComponents';

export type Option<ItemType> = {
    label: string;
    onClick?: (record: ItemType, index: number) => void;
    Icon?: React.ReactNode;
    checkIfVisible?: (record: ItemType) => boolean;
    checkIfIsLoading?: (record: ItemType) => boolean;
    isLoading?: boolean;
};

export type OptionsButtonProps<ItemType> = {
    options: Option<ItemType>[];
    record: ItemType;
    isOptionsLoading?: boolean;
    index: number;
    openOptionsButtonSize?: OpenAppOptionsButtonSize;
    accessibilityLabel?: string;
};

export const OptionsButton = <ItemType,>({
    options,
    record,
    index,
    isOptionsLoading,
    openOptionsButtonSize,
    accessibilityLabel,
}: OptionsButtonProps<ItemType>) => {
    const [isVisiblePopover, setIsVisiblePopover] = useState(false);
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const hidePopover = () => {
        setIsVisiblePopover(false);
        // Return focus to the trigger button after closing
        setTimeout(() => {
            buttonRef.current?.focus();
        }, 0);
    };

    const handleDotsButtonClick = () =>
        setIsVisiblePopover((prevIsVisiblePopover) => !prevIsVisiblePopover);

    const handleOptionClick = (onClick: Option<ItemType>['onClick']) => {
        onClick?.(record, index);
        hidePopover();
    };

    // Handle keyboard navigation for menu options
    const handleOptionKeyDown = (
        event: React.KeyboardEvent,
        optionIndex: number,
        onClick?: Option<ItemType>['onClick'],
    ) => {
        switch (event.key) {
            case 'Enter':
            case ' ':
                event.preventDefault();
                handleOptionClick(onClick);
                break;
            case 'Escape':
                event.preventDefault();
                hidePopover();
                break;
            case 'ArrowDown': {
                event.preventDefault();
                const nextIndex = (optionIndex + 1) % optionRefs.current.length;
                optionRefs.current[nextIndex]?.focus();
                break;
            }
            case 'ArrowUp': {
                event.preventDefault();
                const prevIndex =
                    optionIndex === 0 ? optionRefs.current.length - 1 : optionIndex - 1;
                optionRefs.current[prevIndex]?.focus();
                break;
            }
            default:
                break;
        }
    };

    // Focus management for menu options
    useEffect(() => {
        if (isVisiblePopover && optionRefs.current.length > 0) {
            // Focus the first option when menu opens
            setTimeout(() => {
                optionRefs.current[0]?.focus();
            }, 0);
        }
    }, [isVisiblePopover]);

    const visibleOptions = options.filter((option) =>
        option.checkIfVisible ? option.checkIfVisible(record) : true,
    );

    if (!visibleOptions.length) {
        return null;
    }

    const loadingOptions = visibleOptions.map((option) => {
        if (option.checkIfIsLoading) {
            return option.checkIfIsLoading(record)
                ? { ...option, isLoading: true }
                : { ...option, isLoading: false };
        }
        return option;
    });

    const mappedOptions = loadingOptions.map(({ label, Icon, onClick, isLoading }, optionIndex) => (
        <span key={label}>
            <AppOptionButton
                ref={(el) => {
                    optionRefs.current[optionIndex] = el;
                }}
                ghost
                icon={Icon && <AppOptionIconContainer>{Icon}</AppOptionIconContainer>}
                fullWidth
                onClick={() => handleOptionClick(onClick)}
                onKeyDown={(event) => handleOptionKeyDown(event, optionIndex, onClick)}
                size="small"
                loading={isLoading}
                accessibilityLabel={label}
                role="menuitem"
                tabIndex={isVisiblePopover ? 0 : -1}
            >
                {label}
            </AppOptionButton>
            <AppDivider withoutLast />
        </span>
    ));

    return (
        <AppOptionsButton
            ref={buttonRef}
            optionButtons={mappedOptions}
            isVisiblePopover={isVisiblePopover}
            isOptionsLoading={isOptionsLoading}
            openOptionsButtonSize={openOptionsButtonSize}
            onPopoverHide={hidePopover}
            onDotsButtonClick={handleDotsButtonClick}
            accessibilityLabel={accessibilityLabel}
        />
    );
};
